import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  CogIcon,
  UserCircleIcon,
  BellIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  PencilIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useWallet } from '../../context/WalletContext';
import { api } from '../../services/api';

const ProfilePage: React.FC = () => {
  const { address, tier } = useWallet();
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    bio: '',
    twitter: '',
    discord: '',
    website: '',
    notifyBidOutbid: true,
    notifyAuctionEnd: true,
    notifyWatchlist: true,
    notifyNewsletter: false,
    showActivity: true,
    showCollection: true,
  });

  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      await api.updateProfile({
        username: formData.displayName || undefined,
        email: formData.email || undefined,
        bio: formData.bio || undefined,
        twitter: formData.twitter || undefined,
        discord: formData.discord || undefined,
        website: formData.website || undefined,
        avatar: avatarPreview || undefined,
      } as any);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err?.message || 'Error al guardar. Comprueba que estás conectado.');
    } finally {
      setIsSaving(false);
    }
  };

  const getTierColor = (t: string) => {
    switch (t) {
      case 'PLATINUM': return 'from-slate-300 to-indigo-400';
      case 'GOLD': return 'from-yellow-400 to-amber-500';
      case 'SILVER': return 'from-slate-400 to-slate-300';
      case 'BRONZE': return 'from-amber-700 to-amber-600';
      default: return 'from-dark-400 to-dark-500';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <CogIcon className="w-8 h-8 text-primary-400" />
          Mi Perfil
        </h1>
        <p className="text-gray-400">Gestiona tu información y preferencias</p>
      </div>

      <div className="space-y-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-dark-800/50 rounded-xl border border-dark-700 p-6"
        >
          <div className="flex items-start gap-6">
            {/* Avatar con upload */}
            <div className="relative flex-shrink-0">
              <div
                onClick={handleAvatarClick}
                className="w-24 h-24 rounded-xl overflow-hidden cursor-pointer group"
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar"
                    className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                  />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${getTierColor(tier)} flex items-center justify-center group-hover:opacity-80 transition-opacity`}>
                    <UserCircleIcon className="w-12 h-12 text-white" />
                  </div>
                )}
                {/* Overlay hover */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                  <PencilIcon className="w-6 h-6 text-white" />
                </div>
              </div>
              <button
                onClick={handleAvatarClick}
                className="absolute -bottom-2 -right-2 p-2 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <PencilIcon className="w-4 h-4 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-bold text-white">
                  {formData.displayName || 'Usuario Anónimo'}
                </h2>
                <span className={`px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-r ${getTierColor(tier)} text-white`}>
                  {tier || 'Básico'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <code className="text-sm">{address}</code>
                <button
                  onClick={copyAddress}
                  className="p-1 hover:bg-dark-700 rounded transition-colors"
                >
                  {copied ? (
                    <CheckIcon className="w-4 h-4 text-green-400" />
                  ) : (
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-gray-500 text-sm mt-2">
                {formData.bio || 'Sin biografía'}
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Haz clic en el avatar para cambiar tu foto
              </p>
            </div>
          </div>
        </motion.div>

        {/* Basic Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-dark-800/50 rounded-xl border border-dark-700 p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <UserCircleIcon className="w-5 h-5 text-primary-400" />
            Información Básica
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Nombre para mostrar</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="Tu nombre"
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Email (opcional)</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="tu@email.com"
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm text-gray-400 mb-2">Biografía</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Cuéntanos sobre ti..."
                rows={3}
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 resize-none"
              />
            </div>
          </div>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-800/50 rounded-xl border border-dark-700 p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <GlobeAltIcon className="w-5 h-5 text-primary-400" />
            Redes Sociales
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Twitter</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">@</span>
                <input
                  type="text"
                  value={formData.twitter}
                  onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                  placeholder="username"
                  className="w-full pl-8 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Discord</label>
              <input
                type="text"
                value={formData.discord}
                onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
                placeholder="user#1234"
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://"
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        </motion.div>

        {/* Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-dark-800/50 rounded-xl border border-dark-700 p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <BellIcon className="w-5 h-5 text-primary-400" />
            Notificaciones
          </h3>

          <div className="space-y-4">
            {[
              { key: 'notifyBidOutbid', label: 'Cuando alguien supere mi puja' },
              { key: 'notifyAuctionEnd', label: 'Cuando una subasta esté por terminar' },
              { key: 'notifyWatchlist', label: 'Actividad en mi watchlist' },
              { key: 'notifyNewsletter', label: 'Newsletter y novedades' },
            ].map((item) => (
              <label key={item.key} className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300">{item.label}</span>
                <div
                  className="relative"
                  onClick={() => setFormData({ ...formData, [item.key]: !formData[item.key as keyof typeof formData] })}
                >
                  <div className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    formData[item.key as keyof typeof formData] ? 'bg-primary-500' : 'bg-dark-600'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      formData[item.key as keyof typeof formData] ? 'translate-x-5' : 'translate-x-0.5'
                    } mt-0.5`} />
                  </div>
                </div>
              </label>
            ))}
          </div>
        </motion.div>

        {/* Privacy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-dark-800/50 rounded-xl border border-dark-700 p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <ShieldCheckIcon className="w-5 h-5 text-primary-400" />
            Privacidad
          </h3>

          <div className="space-y-4">
            {[
              { key: 'showActivity', label: 'Mostrar mi actividad públicamente' },
              { key: 'showCollection', label: 'Mostrar mi colección públicamente' },
            ].map((item) => (
              <label key={item.key} className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300">{item.label}</span>
                <div
                  className="relative"
                  onClick={() => setFormData({ ...formData, [item.key]: !formData[item.key as keyof typeof formData] })}
                >
                  <div className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    formData[item.key as keyof typeof formData] ? 'bg-primary-500' : 'bg-dark-600'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      formData[item.key as keyof typeof formData] ? 'translate-x-5' : 'translate-x-0.5'
                    } mt-0.5`} />
                  </div>
                </div>
              </label>
            ))}
          </div>
        </motion.div>

        {/* Feedback + Save Button */}
        {saveSuccess && (
          <div className="flex items-center gap-3 p-4 bg-green-500/20 border border-green-500/40 rounded-xl text-green-400">
            <CheckCircleIcon className="w-6 h-6 flex-shrink-0" />
            <p className="font-medium">¡Perfil guardado correctamente!</p>
          </div>
        )}
        {saveError && (
          <div className="flex items-center gap-3 p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-red-400">
            <ExclamationCircleIcon className="w-6 h-6 flex-shrink-0" />
            <p className="text-sm">{saveError}</p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
                Guardando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
